import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api, CartItem } from '../services/api';
import { useAuth } from './AuthContext';

interface CartContextType {
  items: CartItem[];
  loading: boolean;
  addToCart: (item: Omit<CartItem, 'id'>) => Promise<void>;
  removeFromCart: (id: string) => Promise<void>;
  updateYears: (id: string, years: number) => Promise<void>;
  clearCart: () => void;
  refreshCart: () => Promise<void>;
  itemCount: number;
  subtotal: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

// Local storage key for guest cart
const GUEST_CART_KEY = 'louis_domains_cart';

export function CartProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [items, setItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(false);

  // Load cart on mount and when auth state changes
  useEffect(() => {
    loadCart();
  }, [isAuthenticated]);

  const loadCart = async () => {
    setLoading(true);
    try {
      if (isAuthenticated) {
        // Fetch from API for logged-in users
        const response = await api.getCart();
        setItems(response.items || []);
        // Clear local storage if logged in
        localStorage.removeItem(GUEST_CART_KEY);
      } else {
        // Load from local storage for guests
        const stored = localStorage.getItem(GUEST_CART_KEY);
        if (stored) {
          setItems(JSON.parse(stored));
        }
      }
    } catch (error) {
      console.error('Failed to load cart:', error);
      // Fallback to local storage on error
      const stored = localStorage.getItem(GUEST_CART_KEY);
      if (stored) {
        setItems(JSON.parse(stored));
      }
    } finally {
      setLoading(false);
    }
  };

  const saveLocalCart = (newItems: CartItem[]) => {
    localStorage.setItem(GUEST_CART_KEY, JSON.stringify(newItems));
  };

  const addToCart = async (item: Omit<CartItem, 'id'>) => {
    try {
      if (isAuthenticated) {
        const response = await api.addToCart(item);
        if (response.item) {
          setItems([...items, response.item]);
        }
      } else {
        // Add locally for guests
        const newItem: CartItem = {
          ...item,
          id: `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        };
        const newItems = [...items, newItem];
        setItems(newItems);
        saveLocalCart(newItems);
      }
    } catch (error) {
      console.error('Failed to add to cart:', error);
      throw error;
    }
  };

  const removeFromCart = async (id: string) => {
    try {
      if (isAuthenticated) {
        await api.removeFromCart(id);
        setItems(items.filter(item => item.id !== id));
      } else {
        const newItems = items.filter(item => item.id !== id);
        setItems(newItems);
        saveLocalCart(newItems);
      }
    } catch (error) {
      console.error('Failed to remove from cart:', error);
      throw error;
    }
  };

  const updateYears = async (id: string, years: number) => {
    if (years < 1 || years > 10) return;

    try {
      if (isAuthenticated) {
        await api.updateCartItem(id, years);
      }
      const newItems = items.map(item =>
        item.id === id ? { ...item, years } : item
      );
      setItems(newItems);
      if (!isAuthenticated) {
        saveLocalCart(newItems);
      }
    } catch (error) {
      console.error('Failed to update cart item:', error);
      throw error;
    }
  };

  const clearCart = () => {
    setItems([]);
    localStorage.removeItem(GUEST_CART_KEY);
  };

  const refreshCart = async () => {
    await loadCart();
  };

  const itemCount = items.length;
  const subtotal = items.reduce((sum, item) => sum + item.price * item.years, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        loading,
        addToCart,
        removeFromCart,
        updateYears,
        clearCart,
        refreshCart,
        itemCount,
        subtotal,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
